import type { EntityLocationDTO } from '@user/store/map/typings'
import type { HereMapClickEvent } from '@user/components/HereMap/HereMapNew/hooks/useHereMapInstance'
import { MapPinTypeVariant } from '@user/components/HereMap/MapPin/types'

/**
 * The knock-status-only preview bubble is a snapshot: it is built once, from the
 * clicked badge (`convertEntityLocationToHereEvent`) or from the reverse geocode
 * of an empty-rooftop click, and nothing re-runs that routing while the bubble
 * stays pinned open. So a Lead / Opportunity created for the same address in
 * PestPac or in a second Sales Center tab arrives on the next tile refresh (the
 * refetch `useRefreshOnTabFocus` runs when the user returns to the map) and
 * renders as its own pin, while the stale knock-status bubble keeps sitting over
 * it until dismissed — two preview bubbles at one address (SH-1091).
 *
 * This resolves the record pin that has since appeared under the open
 * knock-status bubble, so the caller can promote it to that record's preview
 * bubble — the same bubble a click on the new pin would have opened.
 *
 * The `KnockStatusLocationId` is the match key, because it is what the two pins
 * genuinely share: the backend stamps the knock status onto the new record's
 * ContactLocation when it is created at a coordinate that already has one
 * (`LeadService.AddContactLocationForLead`), and onto PestPac pins, which have
 * no ContactLocation of their own, in `AttachKnockStatusesToPestPacPins`.
 * Coordinates are not usable here: PestPac geocodes the address itself and can
 * land metres away from the HERE rooftop coordinate the bubble was opened at.
 *
 * @returns the record pin to promote, or null to leave the bubble alone.
 */
export function findRecordPinForOpenKnockStatusBubble(
  knockStatusBubble: HereMapClickEvent | null,
  clickedPin: EntityLocationDTO | null,
  contactLocations: EntityLocationDTO[],
  isHoverPreview: boolean,
): EntityLocationDTO | null {
  if (!knockStatusBubble) return null

  // Hovering a badge is a transient preview that clears itself on mouse-out.
  // Promoting it would pin a bubble the user never clicked open.
  if (isHoverPreview) return null

  const { knockStatusLocationId } = knockStatusBubble
  if (!knockStatusLocationId) return null

  // Only the knock-status-only bubble is ours to promote. The PestPac bubble is
  // opened for a pin that already is a record, and is left to its own sync.
  const bubbleEntityType = knockStatusBubble.entityType?.toLowerCase()
  if (bubbleEntityType && bubbleEntityType !== MapPinTypeVariant.KnockStatus) {
    return null
  }

  // A record-backed selection already owns the bubble: the entity tooltip is
  // rendering off `clickedPin` and the knock-status bubble is gated off, so
  // there is nothing to swap. Knock-status-only and PestPac pins both carry
  // entityId 0, which is exactly the case that still needs promoting.
  if (clickedPin && clickedPin.entityId !== 0) return null

  return (
    contactLocations.find(
      (location) =>
        location.knockStatusLocationId === knockStatusLocationId &&
        location.entityType?.toLowerCase() !== MapPinTypeVariant.KnockStatus,
    ) ?? null
  )
}
